/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LicensingModule
 */

import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';

import {
    IAviCollectionDataGridConfig,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';

import { TenantUsageCollection, TTenantUsageCollection } from 'ng/modules/collections/factories';
import { L10nService } from '@vmw/ngx-vip';
import { Subject } from 'rxjs';
import { TenantUsageItem } from 'ng/modules/items/factories';
import { TenantItem } from 'ajs/modules/accounts';
import * as globalL10n from 'global-l10n';
import './tenant-usage-list.component.less';

import * as l10n from './tenant-usage-list.l10n';

type TTenantItem = typeof TenantItem;

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Component to display and manage tenant usage list with data grid.
 * @author Shubam Bindal
 */
@Component({
    selector: 'tenant-usage-list',
    templateUrl: './tenant-usage-list.component.html',
})
export class TenantUsageListComponent implements OnInit, OnDestroy {
    /**
     * Grid configuration for tenant usage list.
     */
    public tenantUsageGridConfig: IAviCollectionDataGridConfig;

    /**
     * Tenant usage collection instance.
     */
    public readonly tenantUsageCollection: TenantUsageCollection;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Subject used to unsubscribe observables.
     */
    private readonly ngDestroyed$ = new Subject();

    constructor(
        private readonly l10nService: L10nService,
        @Inject(TenantUsageCollection)
        TenantUsageCollection: TTenantUsageCollection,
        @Inject(TenantItem)
        private readonly TenantItemClass: TTenantItem,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.tenantUsageCollection = new TenantUsageCollection();
    }

    /**
     * @override
     */
    public ngOnDestroy(): void {
        this.tenantUsageCollection.destroy();
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }

    /** @override */
    public ngOnInit(): void {
        this.setTenantUsageListGridConfig();
    }

    /**
     * Set grid configuration with fields and actions.
     * @protected
     */
    private setTenantUsageListGridConfig(): void {
        const { tenantUsageCollection, l10nService } = this;

        this.tenantUsageGridConfig = {
            id: 'tenant-usage-list-page',
            collection: tenantUsageCollection,
            fields: [
                {
                    label: l10nService.getMessage(l10nKeys.columnTenantTitle),
                    id: 'tenant_name',
                    transform: (row: TenantUsageItem): string => row.tenantName,
                },
                {
                    label: l10nService.getMessage(l10nKeys.columnUsedTitle),
                    id: 'used_unit',
                    transform: (row: TenantUsageItem): string => String(row.usedUnit),
                },
                {
                    label: l10nService.getMessage(l10nKeys.columnLimitTitle),
                    id: 'unit_limit',
                    transform: (row: TenantUsageItem): string => String(row.unitLimit),
                },
            ],
            layout: {
                placeholderMessage: l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
                hideCreate: true,
                hideEdit: true,
                hideDelete: true,
                hideReferenceCheckAction: true,
                hideSearch: true,
            },
            singleactions: [
                {
                    id: 'edit',
                    label: l10nService.getMessage(l10nKeys.actionTitleEdit),
                    onClick: (row: TenantUsageItem) => this.handleEditTenantUsage(row),
                },
            ],
        };
    }

    /**
     * Create a TenantItem instance with the tenant name and open the edit modal.
     */
    private handleEditTenantUsage(row: TenantUsageItem): void {
        if (!row) {
            return;
        }

        const tenantItem = new this.TenantItemClass({
            id: row.tenantUuid,
        });

        // Load the tenant data and open the edit modal
        tenantItem.load().then(() => {
            tenantItem.edit().then(() => {
                this.tenantUsageCollection.load();
            });
        }).finally(() => {
            tenantItem.destroy();
        });
    }
}
